import { Injectable, Injector } from "@angular/core";
import { UserIntegrationResponse } from "../models/user-integration-response.model";
import { Router } from "@angular/router";
import { DwMessageSeverity, DwMessagingService, DwModalButtonStandardConfigs, DwModalConfig, DwModalService, DwModalSize, DwSecurityStateService, getParameterByName } from "@devwareapps/devware-cap";
import { IntegrationRequestToken } from "../models/integration-request-token.model";
import { SchoolIntegrationResponse, SchoolIntegrationResponseType } from "../models/school-integration-response.model";
import { SchoolIntegrationCompletionRequest } from "../models/school-integration-completion-request.model";
import { BehaviorSubject, Observable } from "rxjs";
import { CreateSchoolModalComponent } from "../components/create-school-modal/create-school-modal.component";
import { filter, mapTo, take } from "rxjs/operators";
import { IntegrationRepositoryService } from "./integration-repository.service";
import { GoogleTagManagerService } from "../google-tag-manager/google-tag-manager.service";
import { JoinRequestPayload } from "../models/join-request-payload.model";
import { SchoolSignupPayload } from "../models/school-signup-payload.model";
import { BrandingPreviewPayload } from "../models/branding-preview-payload.model";


@Injectable({
    providedIn: 'root'
})
export class IntegrationUtilService {
    private static readonly brandingPreviewStorageKey = 'aoBrandingPreviewPayload';
    private static readonly brandingPreviewSessionKey = 'aoBrandingPreviewEnabled';
    private static readonly integrationTokenSessionKey = 'aoIntegrationTokenParams';

    private static lastToken: string = '';
    private static lastType: string = '';
    private static lastStripeSessionToken: string = '';
    private static lastJoinRequestToken: string = '';
    private static brandingPreviewEnabled: boolean = false;

    private static hasLoggedIn: boolean = false;
    integrationRepository: IntegrationRepositoryService;
    static lastSchoolSignupToken: any;
    private readonly integrationWorkflowActive = new BehaviorSubject<boolean>(false);
    private readonly handledIntegrationResponses = new Set<string>();
    private integrationWorkflowVersion = 0;

    constructor(private router: Router,
       // private dwRoutingService: DwRoutingService,
        private dwMessagingService: DwMessagingService,
        private dwModalService: DwModalService,
        //private paymentRepositoryService: PaymentsRepositoryService,
        private injector: Injector,
        private dwSecurityStateService: DwSecurityStateService,
        private googleTagManagerService: GoogleTagManagerService
        //private httpClient: HttpClient
    ) {
        this.setupIntegrationTokens();

        this.dwSecurityStateService.securityStateObservable.subscribe(o => {
            // Don't do anything if the user has never logged in
            if (!IntegrationUtilService.hasLoggedIn) {

                if (o.isAuthenticated) {
                    IntegrationUtilService.hasLoggedIn = true;

                    if (IntegrationUtilService.lastStripeSessionToken) {
                        this.googleTagManagerService.gtagConversionEvent();
                    }
                }

                return;
            }

            if (!o.isAuthenticated && !o.isAuthenticating) {

                if (IntegrationUtilService.lastToken) {

                    console.log("User has logged out, clearing integration token");
                    // User is not authenticated, so we can clear the token and type
                    IntegrationUtilService.lastToken = null;
                    IntegrationUtilService.lastType = null;
                    IntegrationUtilService.lastStripeSessionToken = null;
                    IntegrationUtilService.lastJoinRequestToken = null;
                    this.clearStoredIntegrationToken();
                }
            }
        });
    }

    setupIntegrationTokens() {
        const token = getParameterByName(this.router, "token");
        const type = getParameterByName(this.router, "type");

        if (token && type) {
            IntegrationUtilService.lastToken = token;
            IntegrationUtilService.lastType = type;
            this.storeIntegrationToken(type, token);
        } else if (token || type) {
            IntegrationUtilService.lastToken = null;
            IntegrationUtilService.lastType = null;
            this.clearStoredIntegrationToken();
        } else if (!IntegrationUtilService.lastToken || !IntegrationUtilService.lastType) {
            this.restoreStoredIntegrationToken();
        }

        IntegrationUtilService.lastStripeSessionToken = getParameterByName(this.router, "stripe-session");

        IntegrationUtilService.lastJoinRequestToken = getParameterByName(this.router, "join-token");
        IntegrationUtilService.lastSchoolSignupToken = getParameterByName(this.router, "signup");

        const brandingPreview = getParameterByName(this.router, "preview-branding");
        if (brandingPreview === '1') {
            IntegrationUtilService.brandingPreviewEnabled = true;

            try {
                sessionStorage.setItem(IntegrationUtilService.brandingPreviewSessionKey, '1');
            } catch {
            }
        } else if (brandingPreview === '0') {
            IntegrationUtilService.brandingPreviewEnabled = false;

            try {
                sessionStorage.removeItem(IntegrationUtilService.brandingPreviewSessionKey);
            } catch {
            }
        } else {
            try {
                IntegrationUtilService.brandingPreviewEnabled = sessionStorage.getItem(IntegrationUtilService.brandingPreviewSessionKey) === '1';
            } catch {
                IntegrationUtilService.brandingPreviewEnabled = false;
            }
        }
    }

    getIntegrationTokenParam(): string {
        return IntegrationUtilService.lastToken;
    }

    getIntegrationTokenTypeParam(): string {
        return IntegrationUtilService.lastType;
    }

    getIntegrationQueryParams(): { [key: string]: string } {
        const type = this.getIntegrationTokenTypeParam();
        const token = this.getIntegrationTokenParam();

        if (!type || !token) {
            return {};
        }

        return {
            type,
            token
        };
    }

    navigateToAuthPath(path: string): void {
        const queryParams = this.getIntegrationQueryParams();

        if (Object.keys(queryParams).length) {
            this.router.navigateByUrl(this.router.serializeUrl(this.router.createUrlTree([path], { queryParams })));
            return;
        }

        this.router.navigateByUrl(path);
    }

    getJoinRequestTokenParam(): string {
        return IntegrationUtilService.lastJoinRequestToken;
    }

    getSchoolSignupTokenParam(): string {
        return IntegrationUtilService.lastSchoolSignupToken;
    }

    getIntegrationTokenDetails(): IntegrationRequestToken {
        // Check if there is a type and token so we can pass it to the client
        const token = this.getIntegrationTokenParam();

        if (token) {
            try {
                const tokenJson = atob(token);

                const tokenDetails: IntegrationRequestToken = JSON.parse(tokenJson);

                tokenDetails.integrationType = this.getIntegrationTokenTypeParam();
                tokenDetails.token = token;
                return tokenDetails;
            } catch (e) {
                console.error(e);
            }
        }

        return null;
    }

    getJoinRequestPayload(): JoinRequestPayload {
        const joinRequestToken = this.getJoinRequestTokenParam();

        if (joinRequestToken) {
            try {
                return this.convertResponse<JoinRequestPayload>(joinRequestToken);
            } catch (e) {
                console.error(e);
            }
        }

        return null;
    }

    getSchoolSignupToken(): SchoolSignupPayload {
        const token = this.getSchoolSignupTokenParam();

        if (token) {
            try {
                return this.convertResponse<SchoolSignupPayload>(token);
            } catch (e) {
                console.error(e);
            }
        }

        return null;
    }

    getStripeToken(): string {
        return IntegrationUtilService.lastStripeSessionToken || '';
    }

    saveBrandingPreviewPayload(payload: BrandingPreviewPayload): void {
        try {
            localStorage.setItem(IntegrationUtilService.brandingPreviewStorageKey, JSON.stringify(payload || {}));
        } catch {
        }
    }

    getBrandingPreviewPayload(): BrandingPreviewPayload {
        if (!IntegrationUtilService.brandingPreviewEnabled) {
            return null;
        }

        try {
            const payloadJson = localStorage.getItem(IntegrationUtilService.brandingPreviewStorageKey);
            return payloadJson ? JSON.parse(payloadJson) as BrandingPreviewPayload : null;
        } catch {
            return null;
        }
    }

    getBrandingPreviewLoginUrl(): string {
        return this.router.serializeUrl(this.router.createUrlTree(['/login'], {
            queryParams: {
                'preview-branding': 1
            }
        }));
    }

    // getStripeTokenDetails(): Observable<StripeSessionDetailsResult> {
    //     return this.paymentRepositoryService.getStripeSessionDetails(this.getStripToken());
    // }

    handleSchoolIntegrationResponse(schoolIntegrationResponseRaw: string) {
        if (this.handledIntegrationResponses.has(schoolIntegrationResponseRaw)) {
            return;
        }
        const schoolIntegrationResponse = this.convertResponse<SchoolIntegrationResponse>(schoolIntegrationResponseRaw);

        if (!schoolIntegrationResponse) {
            return;
        }

        this.handledIntegrationResponses.add(schoolIntegrationResponseRaw);
        this.integrationWorkflowVersion++;
        this.integrationWorkflowActive.next(true);

        switch (schoolIntegrationResponse.ResponseType) {
            case SchoolIntegrationResponseType.ApproveIntegration:
                this.showApproveIntegrationDialog(schoolIntegrationResponse);

                break;
            case SchoolIntegrationResponseType.CreateNewSchool:

                this.showCreateSchoolModal(schoolIntegrationResponse);
                break;

            case SchoolIntegrationResponseType.IntegrationError:
                this.showIntegrationError(schoolIntegrationResponse);
                break;
        }
    }

    showCreateSchoolModal(schoolIntegrationResponse: SchoolIntegrationResponse) {
        let modalConfig = new DwModalConfig();

        modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;

        modalConfig.buttons[0].buttonText = 'Create School';

        modalConfig.allowFullScreen = false;
        modalConfig.modalSize = DwModalSize.medium;
        // modalConfig.customModalWidth = '400px';
        // modalConfig.customModalHeight = '300px';
        modalConfig.data = schoolIntegrationResponse;

        modalConfig.component = CreateSchoolModalComponent;

        modalConfig.title = 'Create School and Enable Integration';

        const result = this.dwModalService.showModal<any, any>(modalConfig);
        result.subscribe({
            next: modalResult => {
                if (modalResult.cancelled) {
                    this.integrationWorkflowActive.next(false);
                    return;
                }
                setTimeout(() => this.integrationWorkflowActive.next(false), 4500);
            },
            error: () => this.integrationWorkflowActive.next(false)
        });
        return result;
    }

    showApproveIntegrationDialog(schoolIntegrationResponse: SchoolIntegrationResponse) {
        const systemName = schoolIntegrationResponse.IntegrationSystemName || 'External System';

        this.dwModalService.showConfirmDialog({
            showConfirmDialog: true,
            message: `This allows ${systemName} to link users, purchase courses and view course progress. Do you want to proceed?`,
            title: `Enable integration with ${systemName}`,
        }).subscribe((result) => {

            if (result) {
                this.integrationRepository = this.injector.get(IntegrationRepositoryService);
                const request: SchoolIntegrationCompletionRequest = {
                    FlightSchoolName: schoolIntegrationResponse.FlightSchoolName,
                    Location: schoolIntegrationResponse.Location,
                    ResponseType: SchoolIntegrationResponseType.ApproveIntegration,
                    IntegrationRequestToken: schoolIntegrationResponse.IntegrationRequestToken,
                };

                this.integrationRepository.completeSchoolIntegration(request, schoolIntegrationResponse)
                    .subscribe({
                        next: response => {
                            this.integrationRepository.handlecompleteSchoolIntegrationResponse(response, schoolIntegrationResponse);
                            if (response.RedirectUrl) {
                                setTimeout(() => this.integrationWorkflowActive.next(false), 4500);
                            } else {
                                this.integrationWorkflowActive.next(false);
                            }
                        },
                        error: () => {
                            this.integrationWorkflowActive.next(false);
                        }
                    });
            } else {
                this.integrationWorkflowActive.next(false);
            }
        });
    }

    showIntegrationError(schoolIntegrationResponse: SchoolIntegrationResponse) {
        this.dwModalService.showDialog(
            `Integration Error with ${schoolIntegrationResponse.IntegrationSystemName}`,
            `${schoolIntegrationResponse.ErrorMessage}`,
            [DwModalButtonStandardConfigs.instance.okButton]
        ).subscribe(() => this.integrationWorkflowActive.next(false));
    }

    handleUserIntegrationResponse(userIntegrationResponseRaw: string) {
        if (this.handledIntegrationResponses.has(userIntegrationResponseRaw)) {
            return;
        }
        const userIntegrationResponse = this.convertResponse<UserIntegrationResponse>(userIntegrationResponseRaw);

        if (!userIntegrationResponse) {
            return;
        }

        this.handledIntegrationResponses.add(userIntegrationResponseRaw);
        this.integrationWorkflowVersion++;
        this.integrationWorkflowActive.next(true);

        const systemName = userIntegrationResponse.IntegrationSystemName || 'External System';

        if (userIntegrationResponse.ErrorMessage || !userIntegrationResponse.RedirectUrl) {
            this.dwModalService.showDialog(
                `Integration Error with ${systemName}`,
                userIntegrationResponse.ErrorMessage || `We couldn't complete the ${systemName} integration handoff. Please try again from ${systemName}.`,
                [DwModalButtonStandardConfigs.instance.okButton]
            ).subscribe(() => this.integrationWorkflowActive.next(false));
            return;
        }

        this.dwModalService.showConfirmDialog({
            showConfirmDialog: true,
            message: `This allows ${systemName} to access your course progress and purchase courses from their system. Do you want to proceed?`,
            title: `Grant access to ${systemName}`,
        }).subscribe((result) => {
            if (result) {
                this.dwMessagingService.showMessage({ messageBody: userIntegrationResponse.RedirectMessage, severity: DwMessageSeverity.warning });
                // window.open(tokenExchangeResponse.RedirectUrl);

                console.warn("Redirecting to external system for user integration completion", userIntegrationResponse.RedirectUrl);
                location.href = userIntegrationResponse.RedirectUrl;
            } else {
                this.integrationWorkflowActive.next(false);
            }

        });
    }

    waitForIntegrationWorkflow(): Observable<void> {
        return this.integrationWorkflowActive.pipe(
            filter(active => !active),
            take(1),
            mapTo(undefined)
        );
    }

    getIntegrationWorkflowVersion(): number {
        return this.integrationWorkflowVersion;
    }

    private convertResponse<T>(responseRaw: string): T {
        if (!responseRaw) {
            return;
        }

        const responseJson = atob(responseRaw);

        const response = JSON.parse(responseJson);

        //response.Token = responseRaw;

        return response;
    }

    private storeIntegrationToken(type: string, token: string): void {
        try {
            sessionStorage.setItem(IntegrationUtilService.integrationTokenSessionKey, JSON.stringify({ type, token }));
        } catch {
        }
    }

    private restoreStoredIntegrationToken(): void {
        try {
            const tokenParamsJson = sessionStorage.getItem(IntegrationUtilService.integrationTokenSessionKey);
            const tokenParams = tokenParamsJson ? JSON.parse(tokenParamsJson) : null;

            if (tokenParams?.type && tokenParams?.token) {
                IntegrationUtilService.lastType = tokenParams.type;
                IntegrationUtilService.lastToken = tokenParams.token;
            }
        } catch {
        }
    }

    private clearStoredIntegrationToken(): void {
        try {
            sessionStorage.removeItem(IntegrationUtilService.integrationTokenSessionKey);
        } catch {
        }
    }



}
